/**
 * @module Root
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// Core Libraries
import { ApplicationRef, ComponentRef } from '@angular/core';
import { createNewHosts, createInputTransfer, removeNgStyles } from '@angularclass/hmr';
import { InternalStateType, AppStateService } from '@core/root';
// Services
import { WorkerIpcService } from './../core/worker/services/worker-ipc.service';
import { AppCustomPreloader } from '@root/custom-loader';

/* tslint:disable:no-any only-arrow-functions typedef */
// We are not able to get the exact type of variable so need to discuss with concerned developer

export interface StoreType {
  state: InternalStateType;
  restoreInputValues: () => void;
  disposeOldHosts: () => void;
}

/**
 * Application Providers
 * @access public
 * @type {[AppStateService]}
 */
export const APP_PROVIDERS = [AppStateService, AppCustomPreloader];

/**
 * Root Module Abstract Class, used to define the property of the various root modules for all channels
 * @access private
 * @abstract
 */
export abstract class RootModule {
  constructor(
    public appRef: ApplicationRef,
    public appState: AppStateService,
    private ipcService: WorkerIpcService,
    private appCustomPreloader: AppCustomPreloader
  ) {
    this.onLoad();
  }

  /**
   * Hot Module Init - Event when HMR happens
   * @param store
   * @returns void
   */
  public hmrOnInit(store: StoreType): void {
    if (!store || !store.state) {
      return;
    }
    const spacing: number = 2;
    console.log('HMR store', JSON.stringify(store, null, spacing));
    // set state
    this.appState._state = store.state;
    // set input values
    if ('restoreInputValues' in store) {
      const restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    delete store.state;
    delete store.restoreInputValues;
  }

  /**
   * Before HMR event is destroyed
   * @param store
   * @returns void
   */
  public hmrOnDestroy(store: StoreType): void {
    console.log('destroying current worker');
    this.ipcService.terminate();
    const cmpLocation = this.appRef.components.map(
      (cmp: ComponentRef<any>) => cmp.location.nativeElement
    );
    // save state
    const state = this.appState._state;
    store.state = state;
    // recreate root elements
    store.disposeOldHosts = createNewHosts(cmpLocation);
    // save input values
    store.restoreInputValues = createInputTransfer();
    // remove styles
    removeNgStyles();
  }

  /**
   * After HMR event is destroyed
   * @param store
   * @returns void
   */
  public hmrAfterDestroy(store: StoreType): void {
    // display new elements
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }

  /**
   * On Load event, called when application loads
   * @returns void
   */
  public abstract onLoad(): void;
}
